import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Brain, Activity, Heart, Loader2, User, Calendar, Phone, Mail, CheckCircle2, TrendingUp } from "lucide-react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { SubcategoryChart } from "@/components/patient/SubcategoryChart";
import { RecommendationPanel } from "@/components/patient/RecommendationPanel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface LabResultWithSubcategory {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string;
  created_at: string;
  subcategory_id: string | null;
  lab_subcategories?: {
    id: string;
    name: string;
    lab_categories: {
      name: string;
    };
  };
}

export default function SharedPatientView() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Contador animado para score
  const count = useMotionValue(0);
  const rounded = useTransform(count, Math.round);

  useEffect(() => {
    loadSharedData();
  }, [token]);

  useEffect(() => {
    if (data?.analysis?.health_score) {
      animate(count, data.analysis.health_score, { duration: 1.5 });
    }
  }, [data?.analysis?.health_score]);

  const loadSharedData = async () => {
    try {
      // Create a scoped client with the shared token header for RLS
      const { createClient } = await import("@supabase/supabase-js");
      const sharedClient = createClient(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        {
          global: {
            headers: { "x-shared-token": token || "" },
          },
        }
      );

      // Buscar link compartilhado
      const { data: linkData, error: linkError } = await sharedClient
        .from("shared_patient_links")
        .select("*, patients_onboarding(*)")
        .eq("token", token)
        .single();

      if (linkError) throw new Error("Link inválido ou expirado");

      // Verificar expiração
      if (linkData.expires_at && new Date(linkData.expires_at) < new Date()) {
        throw new Error("Este link expirou");
      }

      // Atualizar contador de visualizações
      await sharedClient
        .from("shared_patient_links")
        .update({
          view_count: ((linkData as any).view_count || 0) + 1,
          last_viewed_at: new Date().toISOString()
        })
        .eq("id", linkData.id);

      // Buscar dados do profissional
      const { data: professionalData } = await supabase
        .from("profiles")
        .select("full_name, avatar_url, health_area")
        .eq("id", linkData.professional_id)
        .single();

      // Carregar dados do paciente
      const [analysisRes, labsRes] = await Promise.all([
        supabase
          .from("ai_global_analysis_cache")
          .select("json_result")
          .eq("patient_id", linkData.patient_id)
          .maybeSingle(),

        supabase
          .from("lab_results_onboarding")
          .select(`
            *,
            lab_subcategories(
              id,
              name,
              lab_categories(name)
            )
          `)
          .eq("patient_id", linkData.patient_id)
          .not('subcategory_id', 'is', null)
          .order("collected_at", { ascending: true })
      ]);

      // Agrupar resultados por subcategoria
      const groupedResults = (labsRes.data || []).reduce((acc: any[], result: LabResultWithSubcategory) => {
        if (!result.lab_subcategories) return acc;

        const subcategoryId = result.lab_subcategories.id;
        let group = acc.find(g => g.subcategory.id === subcategoryId);

        if (!group) {
          group = {
            subcategory: {
              id: result.lab_subcategories.id,
              name: result.lab_subcategories.name,
              category_id: "",
              category_name: result.lab_subcategories.lab_categories.name
            },
            results: []
          };
          acc.push(group);
        }

        group.results.push({
          ...result,
          subcategory_name: result.lab_subcategories.name,
          category_name: result.lab_subcategories.lab_categories.name
        });

        return acc;
      }, []);

      setData({
        patient: linkData.patients_onboarding,
        link: linkData,
        professional: professionalData,
        analysis: analysisRes.data?.json_result,
        groupedResults
      });

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate: string | null) => {
    if (!birthDate) return "N/A";
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-purple-600/5">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-red-600 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Link Inválido</h2>
            <p className="text-muted-foreground">{error || "Este link não existe ou expirou"}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const analysis = data.analysis;

  // Blocos visíveis com defaults
  const defaultBlocks = {
    patient_data: true,
    health_score: true,
    indicators: true,
    insights: true,
    health_map: true,
    analysis: true,
    recommendations: true,
    highlights: true,
  };
  const visibleBlocks = data.link.visible_blocks || defaultBlocks;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-purple-600/5 to-blue-600/5">
      {/* Header Sticky com Backdrop Blur */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-background/80 backdrop-blur-lg border-b sticky top-0 z-50 shadow-sm"
      >
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {/* Lado Esquerdo: Logo + Título */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                  Relatório de Saúde
                </h1>
                <p className="text-sm text-muted-foreground">{data.patient.full_name}</p>
              </div>
            </div>

            {/* Lado Direito: Profissional */}
            {data.professional && (
              <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium">{data.professional.full_name}</p>
                  <p className="text-xs text-muted-foreground">{data.professional.health_area || 'Profissional de Saúde'}</p>
                </div>
                <Avatar className="h-12 w-12 border-2 border-primary/20 hidden sm:block">
                  {data.professional.avatar_url && <AvatarImage src={data.professional.avatar_url} />}
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                    {data.professional.full_name?.charAt(0)?.toUpperCase() || "P"}
                  </AvatarFallback>
                </Avatar>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Seção Dados do Profissional - Visível apenas no Mobile */}
      {data.professional && (
        <div className="container mx-auto px-4 pt-6 sm:hidden max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Card className="bg-gradient-to-br from-primary/5 to-purple-600/5 border-primary/20">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16 border-2 border-primary/30">
                    {data.professional.avatar_url && (
                      <AvatarImage src={data.professional.avatar_url} />
                    )}
                    <AvatarFallback className="bg-primary/20 text-primary font-bold text-xl">
                      {data.professional.full_name?.charAt(0)?.toUpperCase() || "P"}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1">
                    <p className="font-semibold text-lg text-foreground">
                      {data.professional.full_name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {data.professional.health_area || 'Profissional de Saúde'}
                    </p>
                    <Badge variant="secondary" className="mt-1 text-xs">
                      Responsável pelo relatório
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Conteúdo Principal */}
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="space-y-8">

          {/* SEÇÃO 1: Dados Cadastrais */}
          {visibleBlocks.patient_data && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Dados do Paciente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Nome Completo</p>
                      <p className="font-semibold">{data.patient.full_name}</p>
                    </div>

                    {data.patient.data_nascimento && (
                      <>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Idade
                          </p>
                          <p className="font-semibold">{calculateAge(data.patient.data_nascimento)} anos</p>
                        </div>

                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Data de Nascimento</p>
                          <p className="font-semibold">
                            {new Date(data.patient.data_nascimento).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </>
                    )}

                    {data.patient.sexo && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Sexo</p>
                        <p className="font-semibold">
                          {data.patient.sexo === 'M' ? 'Masculino' : data.patient.sexo === 'F' ? 'Feminino' : data.patient.sexo}
                        </p>
                      </div>
                    )}

                    {data.patient.cpf && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">CPF</p>
                        <p className="font-semibold font-mono">{data.patient.cpf}</p>
                      </div>
                    )}

                    {data.patient.email && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          E-mail
                        </p>
                        <p className="font-semibold text-sm">{data.patient.email}</p>
                      </div>
                    )}

                    {data.patient.celular && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Celular
                        </p>
                        <p className="font-semibold">{data.patient.celular}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 2: Score de Saúde com Contador Animado */}
          {visibleBlocks.health_score && analysis?.health_score && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="border-2 border-primary/20 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-purple-600/10" />
                <CardContent className="pt-8 pb-6 relative z-10">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="text-center md:text-left">
                      <h2 className="text-3xl font-bold mb-2">Score de Saúde</h2>
                      <p className="text-muted-foreground">
                        Baseado em {analysis?.stats?.records || 0} registros analisados
                      </p>
                    </div>
                    <motion.div
                      className="text-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5, type: "spring" }}
                    >
                      <motion.div className="text-7xl font-bold text-primary">
                        {rounded}
                      </motion.div>
                      <span className="text-2xl text-muted-foreground">/100</span>
                    </motion.div>
                  </div>
                  <Progress value={analysis.health_score} className="h-4 mt-6" />
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 3: Indicadores de Saúde com Gráficos */}
          {visibleBlocks.indicators && data.groupedResults && data.groupedResults.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Indicadores de Saúde
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {data.groupedResults.map((group: any, i: number) => (
                      <motion.div
                        key={group.subcategory.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 + i * 0.05 }}
                      >
                        <SubcategoryChart
                          subcategory={group.subcategory}
                          results={group.results}
                          period="all"
                        />
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 4: Insights e Alertas */}
          {visibleBlocks.insights && analysis?.highlights && analysis.highlights.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-primary" />
                    Insights e Alertas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {analysis.highlights.map((highlight: any, i: number) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + i * 0.05 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <Card className={`border-l-4 ${highlight.type === "warning" ? "border-l-amber-500 bg-amber-50/50" :
                          highlight.type === "success" ? "border-l-green-500 bg-green-50/50" :
                            "border-l-blue-500 bg-blue-50/50"
                          } hover:shadow-lg transition-all`}>
                          <CardContent className="pt-4">
                            <h3 className="font-semibold mb-2 flex items-center gap-2">
                              <Activity className="h-4 w-4" />
                              {highlight.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">{highlight.message}</p>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 5: Mapa de Sistemas de Saúde Expandido */}
          {visibleBlocks.health_map && analysis?.systems && analysis.systems.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-primary" />
                    Mapa de Sistemas de Saúde
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analysis.systems.map((sys: any, i: number) => {
                      const statusColor =
                        sys.status === "excellent" ? "border-t-green-500" :
                          sys.status === "good" ? "border-t-blue-500" :
                            sys.status === "attention" ? "border-t-amber-500" :
                              "border-t-red-500";

                      const hasAlert = sys.alerts > 0;

                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.8 + i * 0.05 }}
                          whileHover={{ scale: 1.03 }}
                        >
                          <Card className={`border-t-4 ${statusColor} hover:shadow-lg transition-all ${hasAlert ? 'animate-pulse' : ''}`}>
                            <CardContent className="pt-4">
                              <h4 className="font-semibold capitalize mb-3">{sys.name}</h4>

                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Métricas</span>
                                  <span className="font-semibold">{sys.metrics}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Status</span>
                                  <Badge variant={
                                    sys.status === "excellent" ? "default" :
                                      sys.status === "good" ? "secondary" :
                                        sys.status === "attention" ? "outline" :
                                          "destructive"
                                  }>
                                    {sys.status === "excellent" ? "Excelente" :
                                      sys.status === "good" ? "Bom" :
                                        sys.status === "attention" ? "Atenção" :
                                          "Crítico"}
                                  </Badge>
                                </div>

                                {hasAlert && (
                                  <Badge variant="destructive" className="w-full justify-center mt-2">
                                    {sys.alerts} alerta{sys.alerts > 1 ? 's' : ''} ativo{sys.alerts > 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 6: Análise Global da IA */}
          {analysis && (visibleBlocks.analysis || visibleBlocks.recommendations || visibleBlocks.highlights) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="space-y-6"
            >
              {/* Resumo Clínico */}
              {visibleBlocks.analysis && analysis.summary && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="h-5 w-5 text-primary" />
                      Análise Clínica Inteligente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {analysis.summary}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Recomendações Priorizadas */}
              {visibleBlocks.recommendations && analysis.recommendations && analysis.recommendations.length > 0 && (
                <RecommendationPanel recommendations={analysis.recommendations} />
              )}

              {/* Destaques Clínicos */}
              {visibleBlocks.highlights && analysis.clinical_highlights && analysis.clinical_highlights.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      Destaques Clínicos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {analysis.clinical_highlights.map((highlight: string, i: number) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1 + i * 0.05 }}
                          className="flex items-start gap-3"
                        >
                          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                          <span className="text-sm text-muted-foreground">{highlight}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="text-center text-sm text-muted-foreground pt-8 border-t space-y-2"
          >
            <p>🔒 Relatório confidencial gerado por MiaSalute</p>
            <p>Este link expira automaticamente em 30 dias</p>
            <p className="text-xs">Visualizado {data.analysis?.view_count || 0} vez(es)</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
